import type { PageContent } from "../../components/PolicyPage";

const content: PageContent = {
  en: [
    {
      heading: "1. Purpose and scope",
      blocks: [
        {
          type: "p",
          text:
            "This Policy explains how XLUNG – Prestação de Serviços em Desenvolvimento de Software Ltda., CNPJ No. 20.344.845/0001-01, with its registered office at Avenida Dom Luís, 1200, Torre I, sl. 805/806, Meireles – Fortaleza (CE), ZIP code 60.160-230, processes personal data in the Xlung App, on the website, and through the support channels linked to the service.",
        },
      ],
    },
    {
      heading: "2. Who is the data subject",
      blocks: [
        {
          type: "p",
          text:
            "The data subject is the natural person to whom the personal data relate. For this App, this may be the registered user, subscriber, visitor, or any person who contacts XLUNG.",
        },
      ],
    },
    {
      heading: "3. Data we may process",
      blocks: [
        {
          type: "p",
          text:
            "Depending on how the App is used, the following may be processed: (a) registration and contact data, such as name and email; (b) account, authentication, and user identifier data; (c) device identifiers and technical information, such as operating system, app version, IP address, and access logs; (d) cookies, events, and browsing or usage data; (e) data about the subscription, purchased product, status, dates, and transaction identifier; and (f) messages and information provided to support. As a rule, XLUNG does not receive card data or any payment method details. All of this is processed by the app store or the chosen payment provider.",
        },
      ],
    },
    {
      heading: "4. Purposes and legal bases",
      blocks: [
        {
          type: "p",
          text:
            "Data are processed only for legitimate, specific, and disclosed purposes, such as: creating and administering the account; providing features and support; processing and validating subscriptions; security, fraud prevention, and logs; analyzing performance and improving the app; sending operational communications; sending marketing; and complying with obligations and defending rights.",
        },
      ],
    },
    {
      heading: "5. Cookies, Analytics, and Firebase",
      blocks: [
        {
          type: "p",
          text:
            "The App and the website may use cookies, SDKs, and similar technologies for authentication, security, operation, measurement, and improvement. Data are pseudonymized or aggregated wherever possible, with a view to safeguarding users.",
        },
      ],
    },
    {
      heading: "6. Data sharing",
      blocks: [
        {
          type: "p",
          text:
            "XLUNG may share strictly necessary data with: the Apple App Store and Google Play; payment or subscription processors; providers of hosting, infrastructure, analytics, authentication, email, notifications, and support; advisors and authorities, where necessary to comply with a legal obligation or to defend rights; and third parties involved in a corporate reorganization, subject to safeguards. Providers must process the data in accordance with contract, applicable instructions, confidentiality, and security measures.",
        },
      ],
    },
    {
      heading: "7. International transfer",
      blocks: [
        {
          type: "p",
          text:
            "Some technology providers may store or access data in other countries. In such cases, XLUNG shall use mechanisms permitted by the LGPD and by ANPD regulation, in addition to appropriate contractual and security measures.",
        },
      ],
    },
    {
      heading: "8. Retention and deletion",
      blocks: [
        {
          type: "p",
          text:
            "Data will be retained for as long as necessary for the disclosed purposes and will thereafter be deleted or anonymized, except for retention authorized by the LGPD, such as compliance with a legal or regulatory obligation, the regular exercise of rights, and the controller’s exclusive use following anonymization. Specific retention periods shall be approved in an internal retention schedule.",
        },
      ],
    },
    {
      heading: "9. Rights of data subjects",
      blocks: [
        {
          type: "p",
          text:
            "Under the LGPD, the data subject may request: confirmation of the existence of processing; access; correction; anonymization, blocking, or deletion of unnecessary or excessive data or data processed in noncompliance; portability, where regulated and applicable; information about data sharing; information about the possibility of withholding consent and its consequences; withdrawal of consent; deletion of data processed with consent, subject to the legal grounds for retention; objection; and review of decisions made solely on the basis of automated processing, where applicable.",
        },
      ],
    },
    {
      heading: "10. How to exercise your rights",
      blocks: [
        {
          type: "p",
          text:
            "Requests may be sent to contact@xlung.net. The user may also delete their account and associated data directly within the App, under Settings. To protect the data subject, XLUNG may request reasonable information to confirm their identity. Such service will be free of charge and will take place within the legal and regulatory deadlines. If XLUNG cannot fully comply, it will provide a justification where applicable.",
        },
      ],
    },
    {
      heading: "11. Information security",
      blocks: [
        {
          type: "p",
          text:
            "XLUNG adopts technical and administrative measures proportionate to the risks to protect data against unauthorized access and accidental or unlawful situations. These may include access controls, encryption in transit, credential management, logging, updates, backups, and vendor management. No system is absolutely secure; for this reason, the measures are continuously assessed and improved.",
        },
      ],
    },
    {
      heading: "12. Security incidents",
      blocks: [
        {
          type: "p",
          text:
            "Incidents will be assessed and handled in accordance with internal procedures. Where an incident may give rise to relevant risk or harm to data subjects, XLUNG will make the communications required to the ANPD and to the data subjects under applicable law and regulation, containing the appropriate information.",
        },
      ],
    },
    {
      heading: "13. Children and adolescents",
      blocks: [
        {
          type: "p",
          text:
            "The Xlung App is intended for healthcare professionals and students aged 18 or over.",
        },
      ],
    },
    {
      heading: "14. Links and third parties",
      blocks: [
        {
          type: "p",
          text:
            "The App may contain links to third-party services. This Policy does not govern processing carried out by third parties as independent controllers. Reading their policies is recommended. This does not remove XLUNG’s responsibilities for processing under its decision or control.",
        },
      ],
    },
    {
      heading: "15. Changes to this Policy",
      blocks: [
        {
          type: "p",
          text:
            "This Policy may be updated to reflect legal, technological, or operational changes. The date of the version will be indicated at the beginning. Material changes will be communicated appropriately, and new consent will be requested where necessary.",
        },
      ],
    },
    {
      heading: "16. Contact and complaints",
      blocks: [
        {
          type: "p",
          text:
            "Questions and requests should be directed to the privacy channel indicated above. The data subject may also file a petition with the National Data Protection Authority (ANPD) and, in consumer relations, with the competent bodies, after seeking a resolution with the controller, as applicable.",
        },
      ],
    },
  ],
  pt: [
    {
      heading: "1. Objetivo e alcance",
      blocks: [
        {
          type: "p",
          text:
            "Esta Política explica como a XLUNG – Prestação de Serviços em Desenvolvimento de Software Ltda., CNPJ nº 20.344.845/0001-01, com sede na Avenida Dom Luís, 1200, Torre I, sl. 805/806, Meireles – Fortaleza (CE) CEP 60.160-230, trata dados pessoais no Xlung App, no site e nos canais de suporte vinculados ao serviço.",
        },
      ],
    },
    {
      heading: "2. Quem é o titular",
      blocks: [
        {
          type: "p",
          text:
            "Titular é a pessoa natural a quem os dados pessoais se referem. Para este Aplicativo, poderá ser o usuário cadastrado, assinante, visitante ou pessoa que entre em contato com a XLUNG.",
        },
      ],
    },
    {
      heading: "3. Dados que podemos tratar",
      blocks: [
        {
          type: "p",
          text:
            "Conforme a forma de uso, poderão ser tratados: (a) dados cadastrais e de contato, como nome e e-mail; (b) dados de conta, autenticação e identificadores de usuário; (c) identificadores do dispositivo e informações técnicas, como sistema operacional, versão do app, endereço IP e registros de acesso; (d) cookies, eventos e dados de navegação ou uso; (e) dados sobre assinatura, produto adquirido, status, datas e identificador da transação; e (f) mensagens e informações fornecidas ao suporte. Em regra, a XLUNG não recebe os dados de cartão ou qualquer meio de pagamento. Tudo é processado por loja do aplicativo ou provedor de pagamento escolhido.",
        },
      ],
    },
    {
      heading: "4. Finalidades e bases legais",
      blocks: [
        {
          type: "p",
          text:
            "Os dados são tratados somente para finalidades legítimas, específicas e informadas, tais como: criar e administrar a conta; fornecer funcionalidades e suporte; processar e validar assinaturas; segurança, prevenção a fraude e logs; analisar desempenho e melhorar o app; enviar comunicações operacionais; enviar marketing; cumprir obrigações e defender direitos.",
        },
      ],
    },
    {
      heading: "5. Cookies, Analytics e Firebase",
      blocks: [
        {
          type: "p",
          text:
            "O Aplicativo e o site poderão utilizar cookies, SDKs e tecnologias semelhantes para autenticação, segurança, funcionamento, medição e melhoria. Os dados são pseudonimizados ou agregados sempre que possível, visando à segurança dos usuários.",
        },
      ],
    },
    {
      heading: "6. Compartilhamento de dados",
      blocks: [
        {
          type: "p",
          text:
            "A XLUNG poderá compartilhar dados estritamente necessários com: Apple App Store e Google Play; processadores de pagamento ou assinatura; fornecedores de hospedagem, infraestrutura, analytics, autenticação, e-mail, notificações e suporte; consultores e autoridades, quando necessário ao cumprimento de obrigação legal ou defesa de direitos; e terceiros envolvidos em reorganização societária, sujeitos a salvaguardas. Os fornecedores devem tratar os dados segundo contrato, instruções aplicáveis, confidencialidade e medidas de segurança.",
        },
      ],
    },
    {
      heading: "7. Transferência internacional",
      blocks: [
        {
          type: "p",
          text:
            "Alguns fornecedores de tecnologia poderão armazenar ou acessar dados em outros países. Nesses casos, a XLUNG deverá utilizar mecanismos admitidos pela LGPD e pela regulamentação da ANPD, além de medidas contratuais e de segurança apropriadas.",
        },
      ],
    },
    {
      heading: "8. Retenção e eliminação",
      blocks: [
        {
          type: "p",
          text:
            "Os dados serão conservados pelo tempo necessário às finalidades informadas e, depois, eliminados ou anonimizados, salvo conservação autorizada pela LGPD, como cumprimento de obrigação legal ou regulatória, exercício regular de direitos e uso exclusivo da controladora com anonimização. Prazos específicos deverão ser aprovados em tabela interna de retenção.",
        },
      ],
    },
    {
      heading: "9. Direitos dos titulares",
      blocks: [
        {
          type: "p",
          text:
            "Nos termos da LGPD, o titular poderá solicitar: confirmação da existência de tratamento; acesso; correção; anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade; portabilidade, quando regulamentada e aplicável; informação sobre compartilhamentos; informação sobre a possibilidade de negar consentimento e suas consequências; revogação do consentimento; eliminação dos dados tratados com consentimento, ressalvadas hipóteses legais de conservação; oposição; e revisão de decisões tomadas unicamente com base em tratamento automatizado, quando aplicável.",
        },
      ],
    },
    {
      heading: "10. Como exercer seus direitos",
      blocks: [
        {
          type: "p",
          text:
            "As solicitações poderão ser enviadas para contact@xlung.net. O usuário também poderá excluir sua conta e os dados associados diretamente no Aplicativo, em Configurações. Para proteger o titular, a XLUNG poderá solicitar informações razoáveis para confirmar sua identidade. O atendimento será gratuito e ocorrerá nos prazos legais e regulamentares. Se não puder atender integralmente, a XLUNG apresentará justificativa, quando cabível.",
        },
      ],
    },
    {
      heading: "11. Segurança da informação",
      blocks: [
        {
          type: "p",
          text:
            "A XLUNG adota medidas técnicas e administrativas proporcionais aos riscos para proteger dados contra acessos não autorizados e situações acidentais ou ilícitas. Entre elas poderão estar controles de acesso, criptografia em trânsito, gestão de credenciais, registros, atualizações, backups e gestão de fornecedores. Nenhum sistema é absolutamente seguro; por isso, as medidas são avaliadas e aprimoradas continuamente.",
        },
      ],
    },
    {
      heading: "12. Incidentes de segurança",
      blocks: [
        {
          type: "p",
          text:
            "Incidentes serão avaliados e tratados conforme procedimento interno. Quando um incidente puder acarretar risco ou dano relevante aos titulares, a XLUNG realizará as comunicações exigidas à ANPD e aos titulares nos termos da legislação e regulamentação aplicáveis, contendo as informações cabíveis.",
        },
      ],
    },
    {
      heading: "13. Crianças e adolescentes",
      blocks: [
        {
          type: "p",
          text:
            "O Xlung App é voltado a profissionais e estudantes da área da saúde, com idade igual ou superior a 18 anos.",
        },
      ],
    },
    {
      heading: "14. Links e terceiros",
      blocks: [
        {
          type: "p",
          text:
            "O Aplicativo poderá conter links para serviços de terceiros. Esta Política não rege tratamentos realizados por terceiros como controladores independentes. Recomenda-se a leitura de suas políticas. Isso não afasta as responsabilidades da XLUNG pelos tratamentos sob sua decisão ou controle.",
        },
      ],
    },
    {
      heading: "15. Alterações desta Política",
      blocks: [
        {
          type: "p",
          text:
            "Esta Política poderá ser atualizada para refletir mudanças legais, tecnológicas ou operacionais. A data da versão será indicada no início. Alterações relevantes serão comunicadas de modo apropriado e novo consentimento será solicitado quando necessário.",
        },
      ],
    },
    {
      heading: "16. Contato e reclamações",
      blocks: [
        {
          type: "p",
          text:
            "Dúvidas e solicitações deverão ser encaminhadas ao canal de privacidade indicado acima. O titular também poderá apresentar petição à Autoridade Nacional de Proteção de Dados e, em relações de consumo, aos órgãos competentes, após buscar solução junto à controladora, conforme aplicável.",
        },
      ],
    },
  ],
};

export default content;
